import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Badge,
  Divider,
  Box,
  Tooltip,
  CircularProgress
} from '@material-ui/core';
import SentimentVeryDissatisfiedIcon from '@material-ui/icons/SentimentVeryDissatisfied';
import {
  setSelectedValuation,
  setTransientValuationInputs,
  setTransientValuationNoBidReason,
  setTransientValuationType,
  setTransientValuationNotes,
  saveUW,
  appNotify,
  calcPropertyUWProforma,
  refreshModelTax,
  setSelectedProformaInputsContainer,
  setTransientValuationAfterTaxRefresh,
  setValuationTypeModalOptions,
  takeValuation
} from 'store/actions';
import {
  isUsersValuationSelector,
  selectedProformaInputsSelector,
  selectedProformaInputsContainerSelector,
  nextTaskSelector,
} from 'store/selectors';
import {
  PropertyUWProformaInput,
  PropertyUWProformaRent,
  PropertyUWProformaSell,
  PropertyUWProformaStrategy
} from '../propertyUnderwrite';
import { UnderwriteStatusChip, TabsWrapper } from 'components/layout';
import { sendClientLog } from 'services/logService';
import { NoteModal } from 'components/underwrite/shared';
import SubmitBidModal from './SubmitBidModal';
import ValuationTypeModal from '../ValuationTypeModal';
import { Constants } from 'utilities/constants';
import WarningIcon from '@material-ui/icons/Warning';
import RefreshTaxModal from './RefreshTaxModal';
import classNames from 'classnames';
import { GetValuationTypeMetadata } from "api/revue/underwriteData";

const PropertyUWProforma = ({
  classes,
  history,
  user,
  userHasUnderwriteReadOnlyRole,
  selectedOpportunity,
  selectedContainer,
  selectedProformaInputsContainer,
  selectedProformaInputs,
  valuations,
  selectedValuation,
  isUsersValuation,
  transientValuation,
  valuationIsDirty,
  shouldCalculateProforma,
  isSavingUW,
  compsLoadingState,
  isRefreshingModelTax,
  nextTask,
  setSelectedValuation,
  setTransientValuationInputs,
  setTransientValuationNoBidReason,
  setTransientValuationType,
  setTransientValuationAfterTaxRefresh,
  setTransientValuationNotes,
  saveUW,
  appNotify,
  calcPropertyUWProforma,
  refreshModelTax,
  setSelectedProformaInputsContainer,
  setValuationTypeModalOptions,
  takeValuation,
  viewUnderwriteModalIsOpen,
  handleModalClose
}) => {
  const [noBidDropdownValue, setNoBidDropdownValue] = useState(selectedValuation?.noBidReason || '');
  const [proformaValuation, setProformaValuation] = useState(selectedValuation); // This state variable stores the transient valution/selected valuation (whatever is shown/editable on screen)
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isSubmitBidModalOpen, setIsSubmitBidModalOpen] = useState(false);
  const [refreshModelTaxOptions, setRefreshModelTaxOptions] = useState({
    isModalOpen: false,
    isValuationTypeModalOpen: false,
    isValuationTypeModalNextClicked: false,
    canRefresh: false,
    requestedPriceParameter: null
  });
  const [defaultTabIndex, setDefaultTabIndex] = useState(getSelectedTabIndex(selectedValuation));
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasInputLimitError, setHasInputLimitError] = useState({
    hoa: false,
    repairEstimate: false,
    rent: false,
    afterRepairValue: false,
    closingCosts: false,
    bidPrice: false,
    taxes: false
  });

  useEffect(() => {
    if (!isUsersValuation) return;
    const updateTabIndexCondition = (
      defaultTabIndex !== getSelectedTabIndex(transientValuation)
    ) || isCalculating;

    if (updateTabIndexCondition) {
      setDefaultTabIndex(getSelectedTabIndex(transientValuation));
      setIsCalculating(false);
    }

    setProformaValuation(transientValuation);
    setNoBidDropdownValue(transientValuation?.noBidReason);
  }, [transientValuation]);

  useEffect(() => { // Triggers when using dropdown to select user valuation. Show transient values for user's own valuation
    const valuation = isUsersValuation ? transientValuation : selectedValuation;
    setProformaValuation(valuation);
    setNoBidDropdownValue(valuation?.noBidReason || '');
    setDefaultTabIndex(getSelectedTabIndex(valuation));
  }, [selectedValuation]);

  function getSelectedTabIndex(proformaValuation) {
    const strategy = proformaValuation?.valuationOutput?.proformaStrategy;

    if (strategy === 'Retail') {
      return 1;
    }
    return 0;
  }

  //#region Refresh Model Tax

  const handleRefreshModelTax = () => {
    const refreshModelTaxOptions = {
      isModalOpen: true,
      canRefresh: proformaValuation?.valuationInput?.bidPrice && proformaValuation?.valuationInput?.bidPrice > 0, // do not allow refresh if it's null or 0 or negative
      requestedPriceParameter: proformaValuation?.valuationInput?.bidPrice
    }

    setRefreshModelTaxOptions(refreshModelTaxOptions);
  }

  const closeRefreshModelTaxModal = () => {
    const refreshModelTaxOptions = {
      isModalOpen: false,
      canRefresh: false,
      requestedPriceParameter: null
    }

    setRefreshModelTaxOptions(refreshModelTaxOptions);
  }

  const handleRefreshTaxConfirmation = () => {
    closeRefreshModelTaxModal();

    // Call saga
    refreshModelTax(selectedOpportunity.id, proformaValuation.valuationInput.bidPrice, refreshModelTaxSuccessfulCallback);
  }

  const refreshModelTaxSuccessfulCallback = (transactionOpportunityId, taxProformaInputs) => {
    if (selectedOpportunity.id === transactionOpportunityId) {
      // Update tax proforma inputs
      updateTaxProformaInputs(taxProformaInputs);

      // Set taxes proforma input to null, force the user to re-evaluate tax input
      if (isUsersValuation) {
        clearTransientValuationTaxInput();
      }
    }
  }

  function updateTaxProformaInputs(taxProformaInputs) {
    setSelectedProformaInputsContainer({ ...selectedProformaInputsContainer, taxProformaInputs });
  }

  function clearTransientValuationTaxInput() {
    // This saga will reset transient valuation's tax input to null
    setTransientValuationAfterTaxRefresh();
  }

  //#endregion

  /**Triggers when user chooses valuation from dropdown */
  const onValuationIndexChange = e => {
    const newSelectedValuation = valuations[e.target.value];
    setSelectedValuation(newSelectedValuation);
  }

  /**Triggers when user click 'Take Valuation' */
  const takeUWClick = () => {
    takeValuation(selectedValuation);
  }

  const myUWClick = () => {
    const userValuation = valuations.find(val => val.user.id === user.id);
    setSelectedValuation(userValuation);
  }

  /**This gets passed into 'PropertyUWProformaInput' component as change handler */
  const handlePropertyUWProformaInputChange = (newInput) => {
    if (isUsersValuation) {
      let newValuationInput = { ...proformaValuation.valuationInput };
      newValuationInput[newInput.inputGroupName] = newInput.selectedRadioValue;
      newValuationInput[newInput.inputGroupName + 'Type'] = newInput.selectedRadioType;

      setTransientValuationInputs(newValuationInput);
    }
  }

  /**This gets passed into 'PropertyUWProformaInput' component to listen to user keydown event, which triggers calculation logic upon 'Enter' keydown*/
  const handlePropertyUWProformaTextInputKeyDown = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      event.stopPropagation();
      handleCalculate();
    }
  }

  /**This gets passed into 'PropertyUWProformaInput' component to handle error*/
  const handleInputError = (errorMsg) => {
    appNotify(errorMsg, 'warning');
  }

  const setInputLimitError = (inputGroup, hasError) => {
    setHasInputLimitError({
      ...hasInputLimitError,
      [inputGroup]: hasError
    });
  }

  const handleNoBidDropdownChange = event => {
    const noBidReason = event.target.value !== '' ? event.target.value : null
    if (isUsersValuation) {
      setTransientValuationNoBidReason(noBidReason);
    }
  }

  /**Open notes modal */
  const onClickNoteSection = () => {
    setIsNoteModalOpen(true);
  }

  const onCancelNotes = () => {
    setIsNoteModalOpen(false);
  }

  const onUpdateNotes = (notesValue) => {
    const notes = notesValue !== '' ? notesValue : null
    if (isUsersValuation) {
      setTransientValuationNotes(notes);
    }
  }

  const handleSubmitBidModalOpen = () => {
    setIsSubmitBidModalOpen(true);
  }

  const handleSubmitBidModalClose = () => {
    setIsSubmitBidModalOpen(false);
  }

  const handleCalculate = () => {
    calcPropertyUWProforma();
    setIsCalculating(true);
    sendClientLog('Info', `Property UW - User clicked Calculate on proforma. txnOpp ${selectedOpportunity.id}`);
  }

  const saveUWClick = async () => {
    sendClientLog('Info', `${viewUnderwriteModalIsOpen ? 'Explore' : 'Property UW'} - User clicked Save on proforma. txnOpp ${selectedOpportunity.id}`);

    const { possibleValues, defaultValue } = await GetValuationTypeMetadata(selectedOpportunity.id)

    if (possibleValues.length > 1) {
      setTransientValuationType(defaultValue)
      setValuationTypeModalOptions({ isOpen: true, possibleValues })
      return
    }

    const saveUWPayload = {
      defaultValue,
      txnOppId: selectedOpportunity.id,
      proformaInputsContainerId: selectedProformaInputs.id,
      transientValuation
    }

    saveUW(saveUWPayload)
  }

  const saveAndNavigateToNextTask = async () => {
    const buttonType = valuationIsDirty ? 'Save and Next' : 'Next';

    sendClientLog('Info', `Property UW - User clicked ${buttonType} on proforma. txnOpp ${selectedOpportunity.id}`);

    const nextTaskId = nextTask ? nextTask.id : null

    if (!valuationIsDirty) {
      // just navigate to next if nothing needs to be saved.
      navigateToNextOpportunity(nextTaskId)
      return
    }

    const { possibleValues, defaultValue } = await GetValuationTypeMetadata(selectedOpportunity.id)

    if (possibleValues.length > 1) {
      setTransientValuationType(defaultValue)
      setValuationTypeModalOptions({ isOpen: true, possibleValues, nextTaskId })
      return
    }

    const saveUWPayload = {
      defaultValue,
      txnOppId: selectedOpportunity.id,
      proformaInputsContainerId: selectedProformaInputs.id,
      transientValuation,
      history,
      nextTaskId
    }

    saveUW(saveUWPayload)
  }

  const navigateToNextOpportunity = (navigateToId) => {
    if (navigateToId) {
      history.push(`${Constants.Routes.UNDERWRITE_PROPERTY}/${navigateToId}`);
    }
  }

  const saveAndCloseModal = async () => {
    const buttonType = valuationIsDirty ? 'Save and Close' : 'Close';

    sendClientLog('Info', `Explore - User clicked ${buttonType} on proforma. txnOpp ${selectedOpportunity.id}`);

    if (!valuationIsDirty) {
      // just close modal if nothing needs to be saved.
      handleModalClose();
      return
    }

    const { possibleValues, defaultValue } = await GetValuationTypeMetadata(selectedOpportunity.id)

    if (possibleValues.length > 1) {
      setTransientValuationType(defaultValue)
      setValuationTypeModalOptions({ isOpen: true, possibleValues })
      handleModalClose();
      return
    }

    const saveUWPayload = {
      defaultValue,
      txnOppId: selectedOpportunity.id,
      proformaInputsContainerId: selectedProformaInputs.id,
      transientValuation,
      history
    }

    saveUW(saveUWPayload)
    handleModalClose();
  }

  const canEdit = isUsersValuation && !selectedContainer?.viewingReadonlyMode;
  const someInputLimitError = Object.keys(hasInputLimitError).some(key => hasInputLimitError[key] === true);
  const foundSelectedValuationIndex = valuations.findIndex(
    val => selectedValuation ? val.id === selectedValuation.id : false
  );
  const shouldShowSubmitBid = (
    selectedOpportunity.channelText === Constants.Channel.MLS
    || selectedOpportunity.channelText === Constants.Channel.OFFMARKET
  ) // Must be MLS or OffMarket
    && (selectedOpportunity.opportunityStatus !== Constants.OpportunityStatus.PRESCREEN // Must not be prescreen...
      || (selectedOpportunity.isCrmOriginated && selectedOpportunity.selectedValuationNoBidReason)); // ...unless it is also from CRM feed and has no bid reason
  const isWithinValidBedBathRange = selectedOpportunity.beds <= 10 && selectedOpportunity.baths <= 10; // AT-2514 Prevent Saving and Submitting if bed/bath > 10

  const baseNoBidDropdownList = [
    "",
    "Adjacent to CRE",
    "Attached",
    "Below % of List Threshold", // only in Initial & PreScreen
    "Double Yellow Road",
    "HOA Restrictions",
    "Lack of Similar Comps",
    "Modular",
    "MSR Retail Home", // only if we have descriptiveRetailFit=true
    "No Matching ICBM Order", // could be smarter in a next version
    "Other Adverse Location",
    "Not Retail Area", // only if we have descriptiveRetailFit=true
    "Pool",
    "Rehab Scope",
    "Rural",
    "Other"
  ];

  const noBidDropdownList = baseNoBidDropdownList.filter(item => {
    if (item === "Below % of List Threshold" && !(selectedOpportunity.opportunityStatus === "Initial" || selectedOpportunity.opportunityStatus === "PreScreen")) {
      return false; // Exclude if not in Initial or PreScreen
    }
    if ((item === "MSR Retail Home" || item === "Not Retail Area") && !selectedOpportunity.descriptiveRetailFit) {
      return false; // Exclude if descriptiveRetailFit is false
    }
    return true; // Include item by default
  });

  const hasRemovedNoBidReason = noBidDropdownValue === null ? false : noBidDropdownList.indexOf(noBidDropdownValue) === -1; // For handling removed no-bid reasons AT-2795
  const isAutoCalcValuation = selectedValuation?.user.name === Constants.Username.AUTOCALC;
  const hasExistingBid = selectedOpportunity.hadSuccessfulPrismPush || selectedOpportunity.hadSuccessfulCrmPush;
  const exceedsMaxBidPrice = (opp, val) => {
    const bid = val?.valuationOutput.bidPrice ?? opp.selectedValuationBid;
    const maxBid = val?.valuationOutput.maxBid ?? opp.selectedValuationMaxBid;
    
    return (
      (opp.hadSuccessfulPrismPush || opp.hadSuccessfulCrmPush) &&
      bid != null &&
      maxBid != null &&
      bid > maxBid
    )
  } ;
  const isBelowB2LFloor = (obj, isVal = false) => {
    const field = isVal ? obj.valuationOutput.bidPricePctOfAskingPrice : obj.selectedValuationBidPctOfAskingPrice;
    const isBelow = selectedContainer && field != null ? field < selectedContainer.bidPushedB2LFloor : false;
    return isBelow;
  }
  return (
    <>
      {transientValuation && selectedValuation &&
        <div className={classes.container}>
          <div className={classes.header}>
            <div className={classes.headerText}>
              <Typography variant='subtitle2' className={classes.proformaHeader}>Pro Forma</Typography>
              {selectedOpportunity.underwriteStatus &&
                <UnderwriteStatusChip underwriteStatus={selectedOpportunity.underwriteStatus} style={{ marginLeft: 8 }} />
              }
            </div>
            <Divider />
          </div>
          <div className={classNames(classes.inputsContainer, selectedContainer?.viewingReadonlyMode && classes.grayBackground)}>
            {valuations.length > 0 && proformaValuation &&
              <>
                <div className={classes.inputs}>
                  <div className={classes.userSelectHeader}>
                    <div className={classes.userSelectLabel}>
                      <Badge badgeContent={valuations.length} color='primary' classes={{ badge: classes.userBadge }} overlap='rectangular'>
                        <Typography variant='body1' className={classes.headers}>Underwrites</Typography>
                      </Badge>
                    </div>
                    {/* Empty div to space icons to the end and keep header centered */}
                    <div></div>
                    <FormControl>
                      <Select
                        value={foundSelectedValuationIndex > -1 ? foundSelectedValuationIndex : ''}
                        onChange={onValuationIndexChange}
                        inputProps={{ name: 'selectedValuationIndex', id: 'valuationsDropdown' }}
                        classes={{ root: classes.userSelectRoot }}
                      >
                        {valuations.map((el, index) => (
                          <MenuItem key={el.id} value={index}>
                            <span
                              style={
                                el.id === 0
                                  ? {
                                      outline: '1px dotted gray',
                                      padding: '0 2px',
                                      display: 'inline-block',
                                    }
                                  : undefined
                              }
                            >
                              {el.user.fullName
                                ? el.user.fullName.toUpperCase()
                                : el.user.name.toUpperCase()}
                            </span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  {!isUsersValuation
                    ? <Paper className={classes.otherUserWarning}>
                      <Typography variant='subtitle2'>You are viewing another user's underwrite.</Typography>
                      {!selectedContainer?.viewingReadonlyMode ? <Typography variant='caption' className={classes.linkStyle} onClick={isAutoCalcValuation ? myUWClick : takeUWClick}>{isAutoCalcValuation ? 'My Underwrite' : 'Take Underwrite'}</Typography> : null}
                    </Paper>
                    : null
                  }

                  {/* Valuation Inputs */}
                  <div className={classes.inputsHeader}>
                    <Typography variant='caption' color='primary' className={classes.headers}>Inputs</Typography>
                    {/* Empty div to space icons to the end and keep header centered */}
                    <div></div>
                  </div>
                  <div>
                    <PropertyUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='hoa'
                      inputSubheader='HOA'
                      inputValue={proformaValuation.valuationInput.hoa}
                      inputValueType={proformaValuation.valuationInput.hoaType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError}
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.hoas : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit} />
                    <PropertyUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='repairEstimate'
                      inputSubheader='Repair'
                      inputValue={proformaValuation.valuationInput.repairEstimate}
                      inputValueType={proformaValuation.valuationInput.repairEstimateType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError}
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.repairEstimates : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      required />
                    <PropertyUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='rent'
                      inputSubheader='Rent'
                      inputValue={proformaValuation.valuationInput.rent}
                      inputValueType={proformaValuation.valuationInput.rentType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError}
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.rents : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      isLoading={compsLoadingState.isLoading}
                      required />
                    <PropertyUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='afterRepairValue'
                      inputSubheader='After Repair Value'
                      inputValue={proformaValuation.valuationInput.afterRepairValue}
                      inputValueType={proformaValuation.valuationInput.afterRepairValueType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError}
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.afterRepairValues : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit}
                      isLoading={compsLoadingState.isLoading}
                      required />
                    <PropertyUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='closingCosts'
                      inputSubheader='Closing Costs'
                      inputValue={proformaValuation.valuationInput.closingCosts}
                      inputValueType={proformaValuation.valuationInput.closingCostsType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError}
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.closingCosts : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft }}
                      isDisabled={!canEdit} />

                    {selectedOpportunity.channelText !== Constants.Channel.AUCTION &&
                      <PropertyUWProformaInput
                        valuationId={proformaValuation.id}
                        inputGroup='bidPrice'
                        inputSubheader='Bid Price'
                        inputValue={proformaValuation.valuationInput.bidPrice}
                        inputValueType={proformaValuation.valuationInput.bidPriceType}
                        onInputChange={handlePropertyUWProformaInputChange}
                        onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                        onError={handleInputError}
                        setInputLimitError={setInputLimitError}
                        radioValuesArray={selectedProformaInputs ? selectedProformaInputs.bidPrices : []}
                        ancillary={{ sqft: selectedOpportunity?.sqft }}
                        isDisabled={!canEdit}
                        required />
                    }

                    <PropertyUWProformaInput
                      valuationId={proformaValuation.id}
                      inputGroup='taxes'
                      inputSubheader='Taxes'
                      inputValue={proformaValuation.valuationInput.taxes}
                      inputValueType={proformaValuation.valuationInput.taxesType}
                      onInputChange={handlePropertyUWProformaInputChange}
                      onKeyDown={handlePropertyUWProformaTextInputKeyDown}
                      onError={handleInputError}
                      setInputLimitError={setInputLimitError}
                      radioValuesArray={selectedProformaInputs ? selectedProformaInputs.taxes : []}
                      ancillary={{ sqft: selectedOpportunity?.sqft, avm: selectedOpportunity?.normalAvm }}
                      onRefreshModelTax={handleRefreshModelTax}
                      isDisabled={!canEdit}
                      isRefreshingModelTax={isRefreshingModelTax}
                      disableTaxRefresh={selectedContainer.disableTaxRefresh}
                      userHasUnderwriteReadOnlyRole={userHasUnderwriteReadOnlyRole}
                      required />
                  </div>

                  {/* Valuation Outputs: Pro Forma */}
                  <div className={classes.proforma}>
                    <div className={classes.outputheaderText}>
                      <Typography variant='caption' color='primary' className={classes.headers}>Outputs</Typography>
                      {proformaValuation.isCompleted && !proformaValuation.isLatest &&
                        <Tooltip title="Investment criteria is stale for this valuation" placement="bottom">
                          <WarningIcon className={classes.warningIcon} style={{ marginLeft: 2 }} />
                        </Tooltip>}
                    </div>

                    {proformaValuation?.valuationOutput && proformaValuation.valuationOutput.isSuccess !== false // Id of 0 indicates no valuation in db yet, show blank proforma instead of error
                      ? <TabsWrapper
                        labels={['Rent', 'Retail']}
                        defaultTab={defaultTabIndex}
                        setDefaultTabIndex={setDefaultTabIndex}
                      >
                        <PropertyUWProformaRent valuationOutput={proformaValuation.valuationOutput} shouldCalculateProforma={shouldCalculateProforma} selectedOpportunity={selectedOpportunity} exceedsMaxBidPrice={exceedsMaxBidPrice(selectedOpportunity, proformaValuation)} isBelowB2LFloor={isBelowB2LFloor(proformaValuation, true)} />
                        <PropertyUWProformaSell valuationOutput={proformaValuation.valuationOutput} shouldCalculateProforma={shouldCalculateProforma} selectedOpportunity={selectedOpportunity} />
                      </TabsWrapper>
                      : <div className={classes.proformaErrorPaper}>
                        <div className={classes.proformaErrorContainer}>
                          <SentimentVeryDissatisfiedIcon className={classes.proformaErrorIcon} />
                          <Typography variant='subtitle2' style={{ marginTop: 4, fontSize: '1rem' }}>
                            Calculation failed
                          </Typography>
                          <Typography variant='subtitle2' className={classes.proformaErrorMessage}>
                            {proformaValuation?.valuationOutput?.errorMessage}
                          </Typography>
                        </div>
                      </div>
                    }
                  </div>
                  <Divider />
                  {/* Valuation Outputs: Max bid and ICBM stragety section */}
                  {proformaValuation?.valuationOutput &&
                    <PropertyUWProformaStrategy proformaValuation={proformaValuation} shouldCalculateProforma={shouldCalculateProforma} />
                  }
                  {/* No Bid Reason */}
                  <div className={classes.noBidDropdownSection}>
                    <FormControl className={classes.noBidDropdown}>
                      <InputLabel className={classes.noBidLabel} htmlFor='noBidReasonDropdown'>
                        No Bid Reason
                        {hasRemovedNoBidReason &&
                          <Tooltip title={`No Bid Reasons updated - ${noBidDropdownValue} was removed`} placement='top'>
                            <WarningIcon className={classes.warningIcon} />
                          </Tooltip>
                        }
                      </InputLabel>
                      <Select className={hasRemovedNoBidReason ? classes.disabledNoBidReason : null}
                        value={(noBidDropdownValue) || ''}
                        onChange={handleNoBidDropdownChange}
                        disabled={!canEdit || isSavingUW}
                        inputProps={{ name: 'noBidReason', id: 'noBidReasonDropdown' }}>
                        {noBidDropdownList.map(el => {
                          if (el === '') {
                            return <MenuItem value='' key={el}>None</MenuItem>
                          }
                          return <MenuItem value={el} key={el}>{el}</MenuItem>
                        })}
                      </Select>
                    </FormControl>
                  </div>
                  {/* Notes Input   */}
                  <div className={classes.noteContainer}>
                    <Box borderBottom={1} borderColor="grey" className={classes.noteSection} onClick={onClickNoteSection} disabled={!canEdit}>
                      {proformaValuation.notes
                        ?
                        <Typography className={classes.noteContent} display='block' noWrap>
                          {proformaValuation.notes}
                        </Typography>
                        :
                        <Typography className={classes.notePlaceholder} display='block' noWrap>
                          Take a note...
                        </Typography>}
                    </Box>
                  </div>
                  {/* Buttons */}
                  <div className={classes.buttonContainer}>
                    <Button
                      className={classes.fullWidthButton}
                      disabled={!canEdit || isSavingUW || someInputLimitError}
                      variant='contained'
                      color='primary'
                      onClick={handleCalculate}>
                      Calculate
                    </Button>
                  </div>
                  {viewUnderwriteModalIsOpen
                    /* Button enable logic:
                          Calculate: Enables whenever input values are changed, disables once calculation is performed
                          Save: Enabled if valuation output does not match saved values && output is the result of current input (calculation has been performed). Or enabled if no bid reason selected and does not match saved value (can bypass calculation)
                    */
                    ? <div className={classes.buttonContainer}>
                        <Button
                          className={classes.halfWidthButton}
                          // valuationIsDirty (either input, output, noBidReason or notes is dirty) should allow save to be enbled only when calculation is not required.
                          // In other words, user cannot save when valuation output is not the result of current valuation input
                          disabled={!canEdit || isSavingUW || shouldCalculateProforma || !valuationIsDirty || selectedOpportunity?.isProcessing || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole}
                          variant='contained'
                          color='primary'
                          onClick={saveUWClick}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' />
                            : 'Save'}</Button>
                        <Button
                          className={classes.halfWidthButton}
                          // valuationIsDirty (either input, output, noBidReason or notes is dirty) should allow save to be enbled only when calculation is not required.
                          // In other words, user cannot save when valuation output is not the result of current valuation input
                          disabled={valuationIsDirty ? (!canEdit || isSavingUW || shouldCalculateProforma || selectedOpportunity?.isProcessing || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole) : false}
                          variant={valuationIsDirty ? 'contained' : 'outlined'}
                          color='primary'
                          onClick={saveAndCloseModal}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' /> :
                            valuationIsDirty ?
                              'Save & Exit'
                              : 'Exit'}</Button>
                      </div>
                    : <div className={classes.buttonContainer}>
                        <Button
                          className={classes.halfWidthButton}
                          // valuationIsDirty (either input, output, noBidReason or notes is dirty) should allow save to be enbled only when calculation is not required.
                          // In other words, user cannot save when valuation output is not the result of current valuation input
                          disabled={!canEdit || isSavingUW || shouldCalculateProforma || !valuationIsDirty || selectedOpportunity?.isProcessing || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole}
                          variant='contained'
                          color='primary'
                          onClick={saveUWClick}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' />
                            : 'Save'}</Button>
                        <Button
                          className={classes.halfWidthButton}
                          // valuationIsDirty (either input, output, noBidReason or notes is dirty) should allow save to be enbled only when calculation is not required.
                          // In other words, user cannot save when valuation output is not the result of current valuation input
                          disabled={valuationIsDirty ? (!canEdit || isSavingUW || !nextTask || shouldCalculateProforma || selectedOpportunity?.isProcessing || !isWithinValidBedBathRange || userHasUnderwriteReadOnlyRole) : (!nextTask || isSavingUW)}
                          variant='contained'
                          color='primary'
                          onClick={saveAndNavigateToNextTask}
                          tabIndex={-1}>
                          {isSavingUW ?
                            <CircularProgress size={24} color='inherit' /> :
                            valuationIsDirty ?
                              'Save & Next'
                              : 'Next'}</Button>
                      </div>
                  }
                  {shouldShowSubmitBid &&
                    <div className={classes.buttonContainer}>
                      <Button
                        className={classes.fullWidthButton}
                        disabled={
                          isSavingUW ||
                          valuationIsDirty ||
                          userHasUnderwriteReadOnlyRole ||
                          shouldCalculateProforma ||
                          !selectedValuation.isCompleted ||
                          selectedOpportunity?.isProcessing ||
                          // always disable if the status is SUBMISSIONPENDING. It will be blocked on the backend anyways.
                          selectedOpportunity.underwriteStatus === Constants.UnderwriteStatus.SUBMISSIONPENDING ||
                          selectedOpportunity.selectedValuationId !== selectedValuation.id ||
                          selectedOpportunity.opportunityStatus === Constants.OpportunityStatus.TERMINATED ||
                          selectedOpportunity.opportunityStatus === Constants.OpportunityStatus.COMPLETED ||
                          ((selectedOpportunity.isCrmOriginated || selectedOpportunity.hadSuccessfulCrmPush) // For CRM Feed opps,    // AT-613, add check for hadSuccessfulCrmPush
                            ? selectedOpportunity.opportunityStatus === Constants.OpportunityStatus.PRESCREEN // If in Prescreen,
                              ? (!selectedOpportunity.selectedValuationNoBidReason) // Disabled if no bid reason is null or empty - (meaning we allow push to CRM as long as it has noBidReason)
                              : selectedOpportunity.underwriteStatus == null // If not in prescreen, only disabled if null
                            : (selectedOpportunity.underwriteStatus !== Constants.UnderwriteStatus.SAVEDPRICING
                              && selectedOpportunity.underwriteStatus !== Constants.UnderwriteStatus.AUTOPRICED
                              && selectedOpportunity.underwriteStatus !== Constants.UnderwriteStatus.SUBMISSIONFAILED
                              && selectedOpportunity.underwriteStatus !== Constants.UnderwriteStatus.SUBMITTEDPRICING
                              && selectedOpportunity.underwriteStatus !== Constants.UnderwriteStatus.SUBMITTEDNOBID) // For non-CRM, disabled if not in Saved Pricing or Submitted uw status
                          ) ||
                          !isWithinValidBedBathRange
                        }
                        variant='contained'
                        color='primary'
                        onClick={handleSubmitBidModalOpen}>
                        {selectedOpportunity?.isProcessing
                          ? <><span style={{ marginRight: 6 }}>CRM Push in progress</span><CircularProgress size={24} color='inherit' /></>
                          : (selectedOpportunity.selectedValuationNoBidReason != null
                            ? (hasExistingBid ?
                              'Update No Bid' : 'Submit No Bid')
                            : hasExistingBid ?
                              'Update Bid' : 'Submit Bid')}
                      </Button>
                    </div>
                  }
                </div>
              </>
            }
          </div>
          <RefreshTaxModal
            isModalOpen={refreshModelTaxOptions.isModalOpen}
            handleCancellation={closeRefreshModelTaxModal}
            handleConfirmation={handleRefreshTaxConfirmation}
            canRefresh={refreshModelTaxOptions.canRefresh}
            requestedPriceParameter={refreshModelTaxOptions.requestedPriceParameter}
          />

          <ValuationTypeModal />

          <NoteModal
            isModalOpen={isNoteModalOpen}
            onCancel={onCancelNotes}
            onUpdate={onUpdateNotes}
            value={proformaValuation?.notes}
            isReadOnly={!canEdit}
          />
          {shouldShowSubmitBid &&
            <SubmitBidModal
              isModalOpen={isSubmitBidModalOpen}
              onClose={handleSubmitBidModalClose}
              exceedsMaxBidPrice={exceedsMaxBidPrice}
              isBelowB2LFloor={isBelowB2LFloor}
            />
          }
        </div>
      }
    </>
  )
}

const mapStateToProps = state => {
  const selectedProformaInputs = selectedProformaInputsSelector(state);
  const selectedProformaInputsContainer = selectedProformaInputsContainerSelector(state);
  const valuations = selectedProformaInputs?.valuations || [];
  const userHasUnderwriteReadOnlyRole = state.appState.user.roles.underwriteReadOnly

  return {
    user: state.appState.user,
    userHasUnderwriteReadOnlyRole,
    selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
    selectedContainer: state.propertyUWState.selectedContainer,
    selectedProformaInputsContainer,
    selectedProformaInputs,
    valuations: valuations,
    selectedValuation: state.propertyUWState.selectedValuation,
    isUsersValuation: isUsersValuationSelector(state),
    transientValuation: state.propertyUWState.transientValuation,
    valuationIsDirty: state.propertyUWState.valuationIsDirty,
    shouldCalculateProforma: state.propertyUWState.shouldCalculateProforma,
    isSavingUW: state.propertyUWState.isSavingUW,
    compsLoadingState: state.propertyUWState.compsLoadingState,
    isRefreshingModelTax: state.propertyUWState.isRefreshingModelTax,
    nextTask: nextTaskSelector(state, Constants.Module.UNDERWRITE)
  };
}

const mapDispatchToProps = dispatch => ({
  setSelectedValuation: (valuation, initialize) => dispatch(setSelectedValuation(valuation, initialize)),
  setTransientValuationInputs: transientValuation => dispatch(setTransientValuationInputs(transientValuation)),
  setTransientValuationNoBidReason: noBidReason => dispatch(setTransientValuationNoBidReason(noBidReason)),
  setTransientValuationType: (valuationType) => dispatch(setTransientValuationType(valuationType)),
  setTransientValuationAfterTaxRefresh: () => dispatch(setTransientValuationAfterTaxRefresh()),
  setTransientValuationNotes: notes => dispatch(setTransientValuationNotes(notes)),
  saveUW: (txnOppId, proformaInputsContainerId, transientValuation, onSuccess) => dispatch(saveUW(txnOppId, proformaInputsContainerId, transientValuation, onSuccess)),
  appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options)),
  calcPropertyUWProforma: () => dispatch(calcPropertyUWProforma()),
  refreshModelTax: (txnOppId, bidPrice, callback) => dispatch(refreshModelTax(txnOppId, bidPrice, callback)),
  setSelectedProformaInputsContainer: proformaInputsContainer => dispatch(setSelectedProformaInputsContainer(proformaInputsContainer)),
  setValuationTypeModalOptions: modalOptions => dispatch(setValuationTypeModalOptions(modalOptions)),
  takeValuation: (valuationToTake) => dispatch(takeValuation(valuationToTake))
});

const styles = theme => ({
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  container: {
    gridArea: 'proforma',
    height: '100%',
    backgroundColor: '#fff',
    borderLeft: '2px solid rgba(0, 0, 0, 0.12)',
    paddingBottom: '10px',
    display: 'flex',
    flexDirection: 'column'
  },
  inputsContainer: {
    overflowY: 'auto',
  },
  headers: {
    fontWeight: 'bold',
    fontSize: '14px',
    alignSelf: 'center'
  },
  inputs: {
    marginLeft: '5px',
    marginRight: '5px',
  },
  userSelectHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: theme.spacing(0.5)
  },
  userSelectLabel: {
    display: 'flex'
  },
  inputsHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    marginTop: theme.spacing(0.5)
  },
  proforma: {
    gridArea: 'proforma',
    paddingLeft: '5px',
    paddingRight: '5px',
    marginTop: theme.spacing(0.5)
  },
  header: {
    gridArea: 'expander',
    backgroundColor: '#fff',
    width: '100%',
    top: '0',
    zIndex: '1',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  outputheaderText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start'
  },
  noBidDropdownSection: {
    gridArea: 'noBidDropdown',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  noBidDropdown: {
    width: '90%',
  },
  valuationTypeBidDropdown: {
    width: '100%',
  },
  noBidLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  noteContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '10px 0 10px 0',
  },
  noteSection: {
    width: '90%',
    cursor: 'pointer',
    padding: '5px'
  },
  noteContent: {
    width: '100%',
    height: '100%'
  },
  notePlaceholder: {
    color: 'gray'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  halfWidthButton: {
    flex: '0 0 calc(45% - 4px)',
    margin: '0 4px'
  },
  fullWidthButton: {
    flex: '0 0 90%',
  },
  proformaHeader: {
    color: 'black',
    fontSize: '1rem',
  },
  otherUserWarning: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    textAlign: 'center',
    padding: `${theme.spacing(1)}px 0`,
    backgroundColor: theme.palette.warning.main,
  },
  userSelectRoot: {
    marginLeft: theme.spacing(0.5)
  },
  userBadge: {
    top: 10,
    right: -16
  },
  linkStyle: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: '#0000EE',
  },
  disabledLinkStyle: {
    cursor: 'default',
    textDecoration: 'underline',
    color: 'gray'
  },
  proformaErrorIcon: {
    color: theme.palette.red.main,
    fontSize: '2rem'
  },
  proformaErrorPaper: {
    width: '100%',
    height: 482,
    marginBottom: 8,
    borderRadius: 8,
    border: `2px solid ${theme.palette.red.main}`
  },
  proformaErrorContainer: {
    padding: '64px 16px 16px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  proformaErrorMessage: {
    overflow: 'auto',
    height: '100%',
    marginTop: 48,
    textAlign: 'center'
  },
  warningIcon: {
    fill: theme.palette.warning.text
  },
  errorIcon: {
    fill: theme.palette.red.main
  },
  successIcon: {
    fill: theme.palette.success.main
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  }
});

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PropertyUWProforma);
